import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, cn } from "../../shared/ui";
import type { WorkItemDetail } from "../../shared/types/api";
import { WorkItemStatusBadge, WorkItemTypeBadge } from "./WorkItemTypeBadge";
import { formatHours } from "./workItemVocabulary";

/**
 * The work that belongs to this item (§15).
 *
 * <p>Shown as a flat list of direct children rather than a full tree. The hierarchy is at most
 * three levels deep by construction, so a child's own children are one click away on its page,
 * and a nested tree here would spend indentation on depth that does not exist.
 */
export function WorkItemChildren({
  item,
  canEdit,
  onAdd,
}: {
  item: WorkItemDetail;
  canEdit: boolean;
  onAdd: () => void;
}) {
  const navigate = useNavigate();

  if (item.children.length === 0 && !canEdit) {
    return null;
  }

  const done = item.children.filter((child) => child.status === "DONE").length;

  return (
    <Card>
      <CardHeader
        title={item.workItemType === "EPIC" ? "Work in this epic" : "Subtasks"}
        subtitle={
          item.children.length > 0
            ? `${done} of ${item.children.length} done`
            : undefined
        }
        action={
          canEdit &&
          item.workItemType !== "SUBTASK" && (
            <Button
              variant="ghost"
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              onClick={onAdd}
            >
              Add
            </Button>
          )
        }
      />
      <CardBody className="p-0">
        {item.children.length === 0 ? (
          <p className="px-5 py-4 text-sm text-ink-500">
            Nothing underneath yet. §15 does not require a breakdown — add one when the work is
            genuinely separable, not to satisfy a shape.
          </p>
        ) : (
          <ul>
            {item.children.map((child) => (
              <li key={child.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/work-items/${child.itemKey}`)}
                  className="flex w-full items-center gap-3 border-b border-ink-100 px-5 py-2.5 text-left transition-colors last:border-0 hover:bg-ink-50"
                >
                  <WorkItemTypeBadge type={child.workItemType} />
                  <span className="font-mono text-xs text-ink-500">{child.itemKey}</span>
                  <span
                    className={cn(
                      "flex-1 truncate text-sm",
                      child.status === "DONE"
                        ? "text-ink-400 line-through"
                        : "text-ink-900",
                    )}
                  >
                    {child.title}
                  </span>
                  <span className="tnum shrink-0 text-xs text-ink-500">
                    {formatHours(child.currentEstimateHours)}
                  </span>
                  <span className="shrink-0 text-xs text-ink-500">
                    {child.assigneeName ?? "Unassigned"}
                  </span>
                  <WorkItemStatusBadge status={child.status} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
